// components/data-quality/DateQualityAnalysisChart.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarDay,
  faCheckCircle,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement
);

const DateQualityAnalysisChart = ({ dateQuality, totalBookings }) => {
  // Data for date validity chart
  const validityData = {
    labels: ["Valid Dates", "Invalid Dates"],
    datasets: [
      {
        data: [dateQuality.validDates, dateQuality.invalidDates],
        backgroundColor: [
          "rgba(34, 197, 94, 0.8)", // Green for valid
          "rgba(239, 68, 68, 0.8)", // Red for invalid
        ],
        borderColor: ["rgba(34, 197, 94, 1)", "rgba(239, 68, 68, 1)"],
        borderWidth: 1,
      },
    ],
  };

  // Data for gap issues chart
  const gapData = {
    labels: ["No Date Gaps", "Has Date Gaps"],
    datasets: [
      {
        label: "Bookings Count",
        data: [
          totalBookings - dateQuality.bookingsWithGaps,
          dateQuality.bookingsWithGaps,
        ],
        backgroundColor: [
          "rgba(59, 130, 246, 0.8)", // Blue for no gaps
          "rgba(249, 115, 22, 0.8)", // Orange for gaps
        ],
        borderColor: ["rgba(59, 130, 246, 1)", "rgba(249, 115, 22, 1)"],
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 100,
      },
    ],
  };

  // Options for doughnut chart
  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
        labels: {
          boxWidth: 15,
          font: {
            size: 11,
          },
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return chart.data.labels.map((label, i) => {
              const value = datasets[0].data[i];
              const percentage = ((value / totalBookings) * 100).toFixed(1);
              return {
                text: `${label} (${value}, ${percentage}%)`,
                fillStyle: datasets[0].backgroundColor[i],
                strokeStyle: datasets[0].borderColor[i],
                lineWidth: 1,
                hidden: false,
                index: i,
              };
            });
          },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const percentage = ((value / totalBookings) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  // Options for bar chart
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            const percentage = ((value / totalBookings) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0,
        },
      },
    },
  };

  return (
    <div className="h-full flex flex-col">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <FontAwesomeIcon
            icon={faCalendarDay}
            className="text-blue-600 mr-2"
          />
          Date Quality Analysis
        </h3>
        <div className="flex items-center text-sm">
          <div className="flex items-center text-green-600 mr-3">
            <FontAwesomeIcon icon={faCheckCircle} className="mr-1" />
            <span>Valid: {dateQuality.validDates}</span>
          </div>
          <div className="flex items-center text-red-600">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
            <span>With Gaps: {dateQuality.bookingsWithGaps}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 flex-grow">
        <div className="p-1">
          <Doughnut data={validityData} options={doughnutOptions} />
        </div>
        <div className="p-1">
          <Bar data={gapData} options={barOptions} />
        </div>
      </div>

      <div className="mt-2 text-xs text-gray-500">
        <p>
          Date validity is based on proper sequencing of key dates and having no
          gaps greater than 7 days between sequential dates.
        </p>
      </div>
    </div>
  );
};

export default DateQualityAnalysisChart;
